import { useHandler } from "@redotech/react-util/hook";
import {
  buildAttachmentMarker,
  sanitizeIntercomBody,
  useEscalationSubscription,
  type HumanMessage,
} from "@redotech/redo-merchant-app-common/escalation";
import { AssistantMessageContent } from "@redotech/redo-model/agents/llm-messages";
import { MessageAuthorInfo } from "@redotech/redo-web/ai/chat/contexts/message-author-context";
import { MutableRefObject, useState } from "react";

interface UseHumanMessageHandlerParams {
  addMessage: (message: {
    id: string;
    role: "assistant";
    content: string | AssistantMessageContent[];
  }) => void;
  processedMessageIdsRef: MutableRefObject<Set<string>>;
  authorRegistryRef: MutableRefObject<Map<string, MessageAuthorInfo>>;
  isOpenRef: MutableRefObject<boolean>;
  onClosedPanelMessage?: (message: HumanMessage) => void;
}

function buildAttachmentContent(
  message: HumanMessage,
): AssistantMessageContent[] {
  const contentParts: AssistantMessageContent[] = [];

  const cleanedBody = sanitizeIntercomBody(message.body);
  if (cleanedBody) {
    contentParts.push({ type: "text" as const, text: cleanedBody });
  }

  for (const attachment of message.attachments!) {
    contentParts.push({
      type: "text" as const,
      text: buildAttachmentMarker(attachment),
    });
  }

  return contentParts;
}

export function useHumanMessageHandler({
  addMessage,
  processedMessageIdsRef,
  authorRegistryRef,
  isOpenRef,
  onClosedPanelMessage,
}: UseHumanMessageHandlerParams) {
  const [agentName, setAgentName] = useState<string | null>(null);
  const [agentAvatarUrl, setAgentAvatarUrl] = useState<string | null>(null);

  const handleHumanMessage = useHandler((message: HumanMessage) => {
    if (processedMessageIdsRef.current.has(message.id)) {
      return;
    }
    processedMessageIdsRef.current.add(message.id);

    const firstName = message.authorName?.split(" ")[0] ?? null;
    if (firstName && firstName !== agentName) {
      setAgentName(firstName);
    }
    if (message.authorAvatarUrl && message.authorAvatarUrl !== agentAvatarUrl) {
      setAgentAvatarUrl(message.authorAvatarUrl);
    }

    // When panel is closed, show notification instead of adding to chat
    if (!isOpenRef.current) {
      onClosedPanelMessage?.(message);
      return;
    }

    const content: string | AssistantMessageContent[] =
      message.attachments && message.attachments.length > 0
        ? buildAttachmentContent(message)
        : sanitizeIntercomBody(message.body);

    const messageId = `human-${message.id}`;

    authorRegistryRef.current.set(messageId, {
      authorName: message.authorName?.split(" ")[0] ?? null,
      authorAvatarUrl: message.authorAvatarUrl ?? null,
    });

    addMessage({ id: messageId, role: "assistant", content });
  });

  useEscalationSubscription(handleHumanMessage);

  return { agentName, setAgentName, agentAvatarUrl, setAgentAvatarUrl };
}

export { sanitizeIntercomBody } from "@redotech/redo-merchant-app-common/escalation";
